/**
 * UI theme registry. Themes are applied to the document by setting
 * ``document.documentElement.dataset.theme``; CSS in ``styles.css`` selects
 * on ``[data-theme=...]`` to swap visual chrome. Spectral state colours are
 * deliberately not part of the theme so the science readout stays vivid.
 */

import { readStored, writeStored } from "../domain/storage";

export type UiThemeId = "mac_os_8" | "atari_st";

export interface UiThemeOption {
  id: UiThemeId;
  label: string;
}

export const UI_THEMES: UiThemeOption[] = [
  { id: "mac_os_8", label: "Mac OS 8" },
  { id: "atari_st", label: "Atari ST" },
];

export const UI_THEME_STORAGE_KEY = "sdss-quasar-ui-theme";

export const DEFAULT_UI_THEME: UiThemeId = "mac_os_8";

const VALID_IDS: ReadonlySet<UiThemeId> = new Set(UI_THEMES.map((t) => t.id));

/**
 * Coerce an arbitrary stored value to a known theme id, falling back to the
 * default theme when the input is missing or unrecognised.
 */
export function parseStoredTheme(raw: string | null | undefined): UiThemeId {
  if (raw && (VALID_IDS as ReadonlySet<string>).has(raw)) {
    return raw as UiThemeId;
  }
  return DEFAULT_UI_THEME;
}

/**
 * Apply ``theme`` to the document root so CSS selectors react. Safe to call
 * before React mounts because it only touches the existing ``<html>`` element.
 */
export function applyUiThemeToDocument(theme: UiThemeId): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
}

/**
 * Read the persisted theme from ``localStorage``. Returns the default theme
 * when storage is unavailable (e.g. SSR, privacy mode) or empty.
 */
export function readStoredTheme(): UiThemeId {
  return parseStoredTheme(readStored(UI_THEME_STORAGE_KEY));
}

export function writeStoredTheme(theme: UiThemeId): void {
  writeStored(UI_THEME_STORAGE_KEY, theme);
}
